// The Keeper — narrator lines. See docs/narrator/bible.md for the character and the voice rules.
// Placeholders: {floor} {coins} {best} {title} (guest / tenant / mine, by depth).
// All lines hand-written; keep them short, concrete, and anchored to the run.

export type NarratorTrigger =
  | 'run_start_first' | 'run_start'
  | 'first_blood'
  | 'floor_enter' | 'floor_5' | 'floor_10' | 'floor_15' | 'floor_20' | 'floor_30' | 'floor_40' | 'floor_50' | 'floor_60'
  | 'kill_nodrop' | 'kill_jackpot'
  | 'boss_enter' | 'boss_kill'
  | 'hp_low' | 'hp_critical'
  | 'coins_rich'
  | 'shop_open' | 'shop_reroll_1' | 'shop_reroll_2' | 'shop_reroll_3'
  | 'shop_broke'
  | 'qte_miss_3' | 'qte_hit_5'
  | 'death_floor_1_2' | 'death_floor_3_6' | 'death_floor_7_11' | 'death_floor_12' | 'death_floor_25' | 'death_floor_50'
  | 'death_boss' | 'death_record';

export const NARRATOR_LINES: Record<NarratorTrigger, string[]> = {
  run_start_first: [
    'You came down alone. Good. Company makes men careful.',
    'Wipe your feet. The floor is the only thing down here that gets cleaner.',
    'Everything you see is mine. Everything you pick up is borrowed.',
  ],
  run_start: [
    'Back again. I kept your place.',
    'The last one made it to floor {best}. I am not saying that to encourage you.',
    'Same sword, same stairs, new hope. Two of those will last.',
    'You keep coming back. I have stopped asking why. So should you.',
    'The stairs remember you. They are the only ones.',
  ],
  first_blood: [
    'There. Now it is a fight and not a visit.',
    'It bleeds. So do you, in case that was unclear.',
    'First cut. The rest are cheaper.',
    'In the beginning was the deed. You have read that far, at least.',
    'It felt that. Good. A thing that can be hurt can be owned.',
  ],
  // said in the pause before a fight, on entering a floor (1 in 3 floors)
  floor_enter: [
    'Another floor. They do not get shorter.',
    'Floor {floor}. The ones below are quieter. Not empty. Quieter.',
    'Down again. Nobody has ever gone up.',
    'Floor {floor}. Whoever held it last did not leave a note. They never do.',
    'You are walking faster now. They all do, around here.',
    'This one heard you on the stairs. They all hear you on the stairs.',
    'There was an inscription over the first door. I sanded it off. It discouraged the rent.',
    'Floor {floor}. Two and two make four down here as well. Men have hated me for less.',
    'I am the spirit that says no. You are the one who keeps walking. We are a good pair.',
    'Floor {floor}. You came for the next one. You will always come for the next one. That is the whole contract.',
    'Nothing here is permitted. Everything here is possible. Learn the difference before it does.',
    'Floor {floor}. Give a man bread and he sleeps. Give him a sword and he takes the stairs. I sell no bread.',
    'The moment you want to stay is the moment I collect. Keep wanting the next floor. It is safer.',
    'Floor {floor}. Hell has no walls. This is a house. The difference is that a house has a landlord.',
    'You signed nothing. The stairs signed for you. Every step down is a line on the page.',
    'Floor {floor}. Below, the ones who chose their own suffering. They are the proudest of my tenants.',
    'I never tempt. I open doors. The wanting was yours before I met you.',
    'Floor {floor}. The last {title} here said he was free. He was, for as long as the floor allowed.',
    'A rational man would have turned back at ten. You are not, thank God. I use the word loosely.',
    'Floor {floor}. It is not the descent that ruins men. It is that nobody stops them.',
  ],
  // the kill drop roll: 5% nothing, 20% double coins ("JACKPOT" in the log)
  kill_nodrop: [
    'Nothing on it. The floor takes a share sometimes. Today it took all of it.',
    'Empty pockets. It came down here with nothing too.',
    'No coins. Whatever it wanted, it did not want money. Those are the ones to worry about.',
    'Nothing. The floor is entitled to a tenth. Today it read the contract more carefully.',
  ],
  kill_jackpot: [
    'Double. Do not get used to it. The floor is not.',
    'It was carrying twice the usual. Someone else must have died before you got to it.',
    'Two purses. Everything is permitted, someone said. He was right about theft, at least.',
    'Twice the coins. Gold is the one thing down here that has never asked me for meaning.',
  ],
  floor_5: [
    'Five. Below this, the ones who died did it on purpose.',
    'Five floors, {title}. I have started to notice you. That is not a gift.',
  ],
  floor_10: [
    'Ten. I stop counting here. I start remembering.',
    'Ten. The man who built these stairs turned back at nine. He was wiser than you and still dead.',
  ],
  floor_15: [
    'Fifteen. Nobody has kept me company this long. Do not take it as affection.',
    'Fifteen. I have nothing prepared for this. I will improvise. So will they.',
  ],
  floor_20: [
    'Twenty. Deep enough that the contract is written in you now, not on you.',
    'Twenty, mine. I have moved your name to the other book. The one I keep in blood. Old habit.',
    'Twenty floors. The last one here asked what was at the bottom. I said: you, eventually. He kept going.',
  ],
  floor_30: [
    'Thirty. The ones down here chose their suffering freely. It is the only luxury I ever offered.',
    'Thirty. You have stopped asking whether this is permitted. Good. The question was the leash.',
    'Thirty floors. I told a scholar once that hell has no limits. He asked for the stairs. You remind me of him.',
  ],
  floor_40: [
    'Forty. I built nothing below this. It grew, the way debts do.',
    'Forty. Have you wanted a moment to stay yet? Do not. That is the clause that ends you.',
    'Forty, mine. The ones below have no names left. They traded them for one more floor each.',
  ],
  floor_50: [
    'Fifty. I have never had to say this number aloud. It sounds like one of my lies.',
    'Fifty. Half my house is above you. A man who owes that much is not a tenant. He is a partner.',
    'Fifty floors. They are not getting stronger. You are getting further from the door, and the door was the only mercy.',
  ],
  floor_60: [
    'Sixty. There is no page for this. I am writing on the cover of the book.',
    'Sixty. I do not know what is below here. I want to have been honest once. This is it.',
    'Sixty, mine. If you die now I will be less sorry than curious. That is the closest I come to love.',
  ],
  boss_enter: [
    'This one has a name. It will not ask for yours.',
    'He was a guest once too. He stayed.',
    'This floor is easy. I told the last one the same thing.',
    'Mind him. He does not know he is dead, and he takes it out on visitors.',
    'He keeps this floor for me. He is not paid. That is why he is thorough.',
    'He was a scholar. He wanted everything. I gave him one floor and all the time in the world.',
  ],
  boss_kill: [
    'Well. That is a conversation I will have to have with him.',
    'You took something of mine just now. Keep it. I know where you sleep.',
    'He held that floor for a long time. You held it for a minute. Enjoy the minute.',
  ],
  hp_low: [
    'You are leaking. I would hurry, but hurrying is what got you here.',
    'A quarter left. Enough to be interesting.',
    'I have seen men win from here. Fewer than you would like.',
    'Do not look at the bar. Looking has never filled one.',
    'That much red and you are still swinging. Spite. The most reliable thing in a man.',
    'A quarter. This is where they start bargaining with me. I have never once needed to accept.',
    'Low. A man will wish for death all his life and still duck. Duck.',
  ],
  hp_critical: [
    'One more. Then we talk.',
    'Almost. Whatever you are doing, do it once more or never.',
  ],
  coins_rich: [
    'That is a lot of my money in your pocket. Spend it before I get honest about whose it is.',
    '{coins} coins and still walking. The floor does not like carrying that much. Neither do I.',
  ],
  // The shop: three pieces on the table, the fighter takes ONE for free (that is the deal — what
  // you take is what you die in, and he keeps the rest). Coins only buy a new deal, up to three
  // times, and each deal costs more than the last. Taking/skipping closes the table at once, so
// there is no line for it — table lines stay on the table, fight lines stay in the fight.
  shop_open: [
    'Three on the table. Take one. The other two go back to the dead.',
    'Choose. It costs nothing. The next floor collects.',
    'Take your time. It is the only thing here I do not charge for.',
    'What you carry is what you die in. Choose accordingly.',
    'Everything on the table came off someone who came down these stairs. Pick a size.',
    'One each, {title}. That is the rule. I did not make it, but I enjoy it.',
    'Three. The one on the left belonged to a man who chose the one on the right.',
    'A table, three pieces, one hand. Free choice. I have watched it go wrong in every combination.',
    'Take one. The dead do not mind. They minded at the time, and it did them no good.',
    'Pick. Freedom of choice is the heaviest thing on this table, and it is the only thing I give away.',
    'Three pieces, no prices. The price is the floor number, and you already paid it on the stairs.',
    'Choose the one you would want to be found in. Somebody will find you in it.',
    'Take what you like. No signature required. The stairs took care of that.',
    'One piece, {title}. Men have sold everything for less. I only ask that you carry it down.',
  ],
  shop_reroll_1: [
    'Not to your taste? A new table, then. The same dead, in a different order.',
    'Paid. Do not expect the coins to buy better luck. They buy a second look.',
  ],
  shop_reroll_2: [
    'Again. The price went up. The goods did not.',
    'Two deals. You are looking for something I do not put on tables.',
  ],
  shop_reroll_3: [
    'Third and last. After this you take what is there, like everyone.',
    'You have paid more to look than the table is worth. That is the trade. It always was.',
  ],
  shop_broke: [
    'Not enough for another look. Take one of these; the floor does not wait.',
    'Short. A new table costs more than you carry. So did the old one, once.',
  ],
  qte_miss_3: [
    'You were looking at the wrong thing. It was looking at the right one.',
    'Three openings, three waits. The openings will not.',
  ],
  qte_hit_5: [
    'You found the soft place. They all have one. So do you.',
    'Five clean cuts. Whoever taught you that is dead, and I am starting to see why they bothered.',
  ],
  death_floor_1_2: [
    'Well. That was brief. Wipe your feet on the way out.',
    'Floor {floor}. I will not pretend to be surprised. I will pretend to be sorry.',
    'The stairs took longer than you did.',
  ],
  death_floor_3_6: [
    'Middling. You will do better. That is not a promise, it is a probability.',
    'Floor {floor}. Most stop here. Most is not a compliment.',
    'You got far enough to owe me. We will settle it next time.',
  ],
  death_floor_7_11: [
    'Far enough that I noticed. Not far enough that it matters.',
    'Floor {floor}, tenant. The room is yours until someone better dies in it.',
    'That was a real attempt. I keep those separately.',
  ],
  death_floor_12: [
    'That was deep. I will keep the sword; you will not need it where you are, and I am sentimental.',
    'Floor {floor}. I have started a page for you. It has one line. This one.',
    'Deeper than most of what is buried here. Rest with the good company.',
  ],
  death_floor_25: [
    'Floor {floor}, mine. That is further than the men who built the upper floors ever went to check on them.',
    'Twenty-some floors. You will be talked about. By me. To the next one. As a warning.',
    'That was a real descent. I am keeping the boots. They know the way now.',
  ],
  death_floor_50: [
    'Floor {floor}. I did not have a page for this, so I used the cover. Your name is on my book now. Outside.',
    'That far down and it still ended the same. Everything down here does. You made it take a long time.',
    'Fifty-some floors. When the next one asks what is at the bottom, I will say: you. It will be true for a while.',
  ],
  death_boss: [
    'He does that. It is the only thing he does. Good-bye.',
    'He will forget you by morning. I will not. Small comfort, and I am a small comfort.',
  ],
  death_record: [
    'A new best. Written down. I keep the book; you keep the dying.',
    'Floor {best}. Further than you. I mean the you from before. Same result.',
  ],
};
